import { useEffect, useRef, useState } from "react";
import { InfoWindow } from "@vis.gl/react-google-maps";
import { trpc } from "@/lib/trpc";
import { MapTooltipContent } from "./MapTooltipContent";

// Stable reference — avoids re-triggering InfoWindow's open/close effect cycle
const PIXEL_OFFSET: [number, number] = [0, -8];

interface MarkerData {
  id: string;
  name: string;
  ownership: string;
  confidence: number | null;
  address: string | null;
  sportTypes?: string[] | null;
}

interface MapTooltipProps {
  locationId: string;
  anchor: google.maps.marker.AdvancedMarkerElement;
  markerData: MarkerData;
  onClose: () => void;
  onAddToCollection: (locationId: string) => void;
  onViewDetails: (locationId: string) => void;
}

export function MapTooltip({
  locationId,
  anchor,
  markerData,
  onClose,
  onAddToCollection,
  onViewDetails,
}: MapTooltipProps) {
  const screenshotCacheRef = useRef<Map<string, string>>(new Map());
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(
    screenshotCacheRef.current.get(locationId) ?? null
  );
  const [screenshotLoading, setScreenshotLoading] = useState(
    !screenshotCacheRef.current.has(locationId)
  );

  const utils = trpc.useUtils();

  // Fetch collections for this location
  const { data: collections } = trpc.collections.getForLocation.useQuery(
    { locationId },
    { staleTime: 30_000 }
  );

  useEffect(() => {
    const cached = screenshotCacheRef.current.get(locationId);
    if (cached) {
      setScreenshotUrl(cached);
      setScreenshotLoading(false);
      return;
    }

    setScreenshotLoading(true);
    setScreenshotUrl(null);

    utils.locations.getScreenshotUrl
      .fetch({ locationId })
      .then((result) => {
        screenshotCacheRef.current.set(locationId, result.url);
        setScreenshotUrl(result.url);
      })
      .catch(() => {
        // No screenshot available — that's fine
        setScreenshotUrl(null);
      })
      .finally(() => {
        setScreenshotLoading(false);
      });
  }, [locationId, utils]);

  return (
    <InfoWindow
      anchor={anchor}
      shouldFocus={false}
      headerDisabled
      pixelOffset={PIXEL_OFFSET}
    >
      <MapTooltipContent
        locationId={markerData.id}
        name={markerData.name}
        sportTypes={markerData.sportTypes ?? null}
        ownership={markerData.ownership}
        confidence={markerData.confidence}
        address={markerData.address}
        screenshotUrl={screenshotUrl}
        screenshotLoading={screenshotLoading}
        collections={collections ?? null}
        onAddToCollection={onAddToCollection}
        onClose={onClose}
        onViewDetails={onViewDetails}
      />
    </InfoWindow>
  );
}
